import { useEffect, useState } from 'react';
import { Search, Check, Ban, RotateCcw, X, Store } from 'lucide-react';
import clsx from 'clsx';
import PageHeader from '@/components/PageHeader';
import AccessCountdown from '@/components/AccessCountdown';
import SupplierSubscription from '@/components/SupplierSubscription';
import {
  listSuppliers,
  getSupplier,
  setSupplierStatus,
  type SupplierListItem,
  type SupplierDetail,
  type SupplierStatusKey,
  type SupplierSubFilter,
} from '@/lib/operatorApi';
import { formatDate, formatKop } from '@/lib/format';

const FILTERS: { value: SupplierStatusKey | 'all'; label: string }[] = [
  { value: 'all', label: 'Все' },
  { value: 'pending', label: 'На модерации' },
  { value: 'active', label: 'Активные' },
  { value: 'blocked', label: 'Заблокированы' },
];

const SUB_FILTERS: { value: SupplierSubFilter; label: string }[] = [
  { value: 'all', label: 'Любая подписка' },
  { value: 'expiring', label: 'Истекает / просрочена' },
  { value: 'trial', label: 'Триал' },
  { value: 'active', label: 'Оплачена' },
  { value: 'past_due', label: 'Просрочена' },
  { value: 'cancelled', label: 'Отменена' },
];

const STATUS_BADGE: Record<SupplierStatusKey, { label: string; cls: string }> = {
  pending: { label: 'На модерации', cls: 'badge-amber' },
  active: { label: 'Активен', cls: 'badge-green' },
  blocked: { label: 'Заблокирован', cls: 'badge-red' },
};

const SUB_LABEL: Record<string, string> = {
  trial: 'Триал',
  active: 'Подписка',
  past_due: 'Просрочена',
  cancelled: 'Отменена',
};

const LEGAL_FORM: Record<string, string> = {
  OOO: 'ООО',
  IP: 'ИП',
  SAMOZANYATY: 'Самозанятый',
  AO: 'АО',
};

export default function Suppliers() {
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState<SupplierStatusKey | 'all'>('all');
  const [subFilter, setSubFilter] = useState<SupplierSubFilter>('all');
  const [rows, setRows] = useState<SupplierListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [detail, setDetail] = useState<SupplierDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let alive = true;
    const t = setTimeout(() => {
      setLoading(true);
      setError(null);
      listSuppliers({ q: query || undefined, status: filter, sub: subFilter, limit: 200 })
        .then((r) => {
          if (!alive) return;
          setRows(r.results);
          setTotal(r.total);
        })
        .catch((e: Error) => {
          if (alive) setError(e.message);
        })
        .finally(() => {
          if (alive) setLoading(false);
        });
    }, 250);
    return () => {
      alive = false;
      clearTimeout(t);
    };
  }, [query, filter, subFilter, reloadKey]);

  const showFlash = (msg: string) => {
    setFlash(msg);
    setTimeout(() => setFlash(null), 3500);
  };

  // После операции над подпиской перечитываем карточку и список: срок, статус и
  // «виден в витрине» считает бэкенд, локально их дорисовывать нельзя.
  const refreshDetail = async (msg: string) => {
    showFlash(msg);
    setReloadKey((k) => k + 1);
    if (!detail) return;
    try {
      setDetail(await getSupplier(detail.id));
    } catch (e) {
      setError((e as Error).message);
    }
  };

  const act = async (
    id: string,
    status: 'PENDING' | 'ACTIVE' | 'BLOCKED',
    name: string,
  ) => {
    try {
      setBusyId(id);
      await setSupplierStatus(id, status);
      const verb =
        status === 'ACTIVE' ? 'одобрен' : status === 'BLOCKED' ? 'заблокирован' : 'обновлён';
      showFlash(`Поставщик «${name}» ${verb}`);
      setDetail((d) => (d && d.id === id ? { ...d, status: status.toLowerCase() as SupplierStatusKey } : d));
      setReloadKey((k) => k + 1);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusyId(null);
    }
  };

  const openDetail = async (id: string) => {
    setDetailLoading(true);
    setDetail(null);
    try {
      const d = await getSupplier(id);
      setDetail(d);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setDetailLoading(false);
    }
  };

  return (
    <div>
      <PageHeader
        eyebrow="Поставщики"
        title="Магазин «Закуп»"
        description={`Всего: ${total}. Модерация заявок, одобрение и блокировка поставщиков.`}
      />

      <div className="page-pad">
        {flash && (
          <div className="mb-4 border border-green-600/30 bg-green-50 px-4 py-3 text-[13px] text-green-700">
            {flash}
          </div>
        )}

        <div className="mb-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div className="flex flex-wrap gap-1">
            {FILTERS.map((f) => (
              <button
                key={f.value}
                type="button"
                onClick={() => setFilter(f.value)}
                className={
                  'border px-3 py-1.5 text-[12px] font-medium transition ' +
                  (filter === f.value
                    ? 'border-ink bg-ink text-white'
                    : 'border-line bg-white text-ink/70 hover:border-ink/50')
                }
              >
                {f.label}
              </button>
            ))}
          </div>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center md:w-auto">
            <select
              className="input md:w-52"
              value={subFilter}
              onChange={(e) => setSubFilter(e.target.value as SupplierSubFilter)}
            >
              {SUB_FILTERS.map((f) => (
                <option key={f.value} value={f.value}>
                  {f.label}
                </option>
              ))}
            </select>
            <div className="relative md:w-80">
              <Search
                size={14}
                strokeWidth={1.75}
                className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-ink/40"
              />
              <input
                type="search"
                className="input pl-9"
                placeholder="Поиск по названию, email, ИНН, городу…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
            </div>
          </div>
        </div>

        {error && (
          <div className="mb-4 border border-red-600/30 bg-red-50 px-4 py-3 text-[13px] text-red-700">
            {error}
          </div>
        )}

        {/* Desktop: таблица */}
        <div className="card hidden overflow-hidden md:block">
          <table className="table-grid">
            <thead>
              <tr>
                <th>Поставщик</th>
                <th>Контакты</th>
                <th>Город</th>
                <th className="text-right">Каталог</th>
                <th>Осталось дней</th>
                <th>Статус</th>
                <th className="text-right">Действия</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((s) => (
                <tr key={s.id}>
                  <td>
                    <button
                      onClick={() => openDetail(s.id)}
                      className="text-left font-medium hover:underline"
                    >
                      {s.name}
                    </button>
                    <div className="text-[11px] text-ink/50">
                      {s.legalForm ? `${LEGAL_FORM[s.legalForm] ?? s.legalForm} · ` : ''}
                      Создан {formatDate(s.createdAt)}
                    </div>
                  </td>
                  <td>
                    <div className="text-ink/90">{s.email}</div>
                    <div className="text-[11px] text-ink/50">{s.phone ?? '—'}</div>
                  </td>
                  <td className="text-ink/70">{s.city ?? '—'}</td>
                  <td className="text-right text-[12px] text-ink/80">
                    {s.productsCount} тов.
                    <div className="text-[11px] text-ink/50">{s.categoriesCount} кат.</div>
                  </td>
                  <td>
                    {s.accessEndsAt ? (
                      <>
                        <AccessCountdown
                          endsAt={s.accessEndsAt}
                          subject={s.subscriptionStatus === 'active' ? 'paid' : 'trial'}
                          accessState={s.accessState}
                        />
                        <div className="text-[11px] text-ink/45">
                          до {formatDate(s.accessEndsAt)}
                        </div>
                      </>
                    ) : (
                      <span className="text-[12px] text-ink/40">срок не задан</span>
                    )}
                  </td>
                  <td>
                    <div className="flex flex-col items-start gap-1">
                      <span className={clsx(STATUS_BADGE[s.status].cls)}>
                        {STATUS_BADGE[s.status].label}
                      </span>
                      <span className="text-[10px] text-ink/45">
                        {SUB_LABEL[s.subscriptionStatus] ?? s.subscriptionStatus}
                      </span>
                    </div>
                  </td>
                  <td>
                    <div className="flex items-center justify-end gap-1">
                      <RowActions s={s} busy={busyId === s.id} onAct={act} onOpen={() => openDetail(s.id)} />
                    </div>
                  </td>
                </tr>
              ))}
              {!loading && rows.length === 0 && (
                <tr>
                  <td colSpan={7} className="py-10 text-center text-ink/50">
                    {query || filter !== 'all' || subFilter !== 'all'
                      ? 'Ничего не найдено по фильтру'
                      : 'Поставщиков пока нет'}
                  </td>
                </tr>
              )}
              {loading && rows.length === 0 && (
                <tr>
                  <td colSpan={7} className="py-10 text-center text-ink/40">
                    Загрузка…
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Mobile: карточки */}
        <ul className="card divide-y divide-line md:hidden">
          {rows.map((s) => (
            <li key={s.id} className="px-4 py-4">
              <div className="flex items-start justify-between gap-3">
                <button onClick={() => openDetail(s.id)} className="min-w-0 flex-1 text-left">
                  <div className="truncate text-[15px] font-medium text-ink">{s.name}</div>
                  <div className="mt-0.5 truncate text-[12px] text-ink/60">{s.email}</div>
                </button>
                <span className={clsx(STATUS_BADGE[s.status].cls)}>{STATUS_BADGE[s.status].label}</span>
              </div>
              <dl className="mt-3 grid grid-cols-2 gap-y-1.5 text-[12px]">
                <dt className="text-ink/50">Город</dt>
                <dd className="text-right text-ink/80">{s.city ?? '—'}</dd>
                <dt className="text-ink/50">Каталог</dt>
                <dd className="text-right text-ink/80">
                  {s.productsCount} тов. · {s.categoriesCount} кат.
                </dd>
                <dt className="text-ink/50">Осталось</dt>
                <dd className="flex justify-end text-right text-ink/80">
                  {s.accessEndsAt ? (
                    <AccessCountdown
                      endsAt={s.accessEndsAt}
                      subject={s.subscriptionStatus === 'active' ? 'paid' : 'trial'}
                      accessState={s.accessState}
                    />
                  ) : (
                    '—'
                  )}
                </dd>
              </dl>
              <div className="mt-3 flex flex-wrap justify-end gap-1">
                <RowActions s={s} busy={busyId === s.id} onAct={act} onOpen={() => openDetail(s.id)} />
              </div>
            </li>
          ))}
          {!loading && rows.length === 0 && (
            <li className="px-4 py-10 text-center text-[13px] text-ink/50">
              {query || filter !== 'all' ? 'Ничего не найдено по фильтру' : 'Поставщиков пока нет'}
            </li>
          )}
          {loading && rows.length === 0 && (
            <li className="px-4 py-10 text-center text-[13px] text-ink/40">Загрузка…</li>
          )}
        </ul>
      </div>

      {(detail || detailLoading) && (
        <SupplierModal
          detail={detail}
          loading={detailLoading}
          busy={detail ? busyId === detail.id : false}
          onAct={act}
          onSaved={refreshDetail}
          onClose={() => setDetail(null)}
        />
      )}
    </div>
  );
}

function RowActions({
  s,
  busy,
  onAct,
  onOpen,
}: {
  s: SupplierListItem;
  busy: boolean;
  onAct: (id: string, status: 'PENDING' | 'ACTIVE' | 'BLOCKED', name: string) => void;
  onOpen: () => void;
}) {
  return (
    <>
      {s.status === 'pending' && (
        <button
          type="button"
          disabled={busy}
          onClick={() => onAct(s.id, 'ACTIVE', s.name)}
          className="inline-flex items-center gap-1 border border-green-600 bg-green-600 px-3 py-1.5 text-[12px] font-medium text-white transition hover:bg-green-700 disabled:opacity-50"
        >
          <Check size={13} /> Одобрить
        </button>
      )}
      {(s.status === 'pending' || s.status === 'active') && (
        <button
          type="button"
          disabled={busy}
          onClick={() => onAct(s.id, 'BLOCKED', s.name)}
          className="inline-flex items-center gap-1 border border-red-300 px-3 py-1.5 text-[12px] font-medium text-red-700 transition hover:bg-red-50 disabled:opacity-50"
        >
          <Ban size={13} /> {s.status === 'pending' ? 'Отклонить' : 'Заблокировать'}
        </button>
      )}
      {s.status === 'blocked' && (
        <button
          type="button"
          disabled={busy}
          onClick={() => onAct(s.id, 'ACTIVE', s.name)}
          className="inline-flex items-center gap-1 border border-green-600 px-3 py-1.5 text-[12px] font-medium text-green-700 transition hover:bg-green-50 disabled:opacity-50"
        >
          <RotateCcw size={13} /> Разблокировать
        </button>
      )}
      <button
        type="button"
        onClick={onOpen}
        className="border border-line px-3 py-1.5 text-[12px] text-ink/70 transition hover:border-ink/50"
      >
        Подробнее
      </button>
    </>
  );
}

function SupplierModal({
  detail,
  loading,
  busy,
  onAct,
  onSaved,
  onClose,
}: {
  detail: SupplierDetail | null;
  loading: boolean;
  busy: boolean;
  onAct: (id: string, status: 'PENDING' | 'ACTIVE' | 'BLOCKED', name: string) => void;
  onSaved: (msg: string) => Promise<void> | void;
  onClose: () => void;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center sm:p-4">
      <button className="absolute inset-0 bg-ink/50" onClick={onClose} aria-label="Закрыть" />
      <div className="relative max-h-[92vh] w-full overflow-auto bg-white sm:max-w-2xl">
        <div className="sticky top-0 flex items-center justify-between border-b border-line bg-white px-5 py-4">
          <div className="flex items-center gap-3 min-w-0">
            <div className="grid h-10 w-10 shrink-0 place-items-center overflow-hidden rounded-lg bg-paper text-ink/40">
              {detail?.logoUrl ? (
                <img src={detail.logoUrl} alt="" className="h-full w-full object-cover" />
              ) : (
                <Store size={18} />
              )}
            </div>
            <div className="min-w-0">
              <div className="truncate font-medium text-ink">{detail?.name ?? 'Загрузка…'}</div>
              {detail && (
                <span className={clsx(STATUS_BADGE[detail.status].cls)}>
                  {STATUS_BADGE[detail.status].label}
                </span>
              )}
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 text-ink/50 hover:text-ink">
            <X size={18} />
          </button>
        </div>

        {loading || !detail ? (
          <div className="py-16 text-center text-ink/40">Загрузка…</div>
        ) : (
          <div className="space-y-5 p-5">
            {detail.description && <p className="text-[13px] text-ink/70">{detail.description}</p>}

            <SupplierSubscription detail={detail} onSaved={onSaved} />

            <Section title="Контакты">
              <Row label="Email">{detail.email}</Row>
              <Row label="Телефон">{detail.phone ?? '—'}</Row>
              <Row label="Город">{detail.city ?? '—'}</Row>
              <Row label="Контактное лицо">{detail.contactPerson ?? '—'}</Row>
            </Section>

            <Section title="Юридические реквизиты">
              <Row label="Форма">{detail.legalForm ? LEGAL_FORM[detail.legalForm] ?? detail.legalForm : '—'}</Row>
              <Row label="Наименование">{detail.legalName ?? '—'}</Row>
              <Row label="ИНН">{detail.inn ?? '—'}</Row>
              <Row label="КПП">{detail.kpp ?? '—'}</Row>
              <Row label="ОГРН">{detail.ogrn ?? '—'}</Row>
              <Row label="Налогообложение">{detail.taxSystem ?? '—'}</Row>
              <Row label="Юр. адрес">{detail.legalAddress ?? '—'}</Row>
              <Row label="Адрес склада">{detail.warehouseAddress ?? '—'}</Row>
            </Section>

            <Section title="Банковские реквизиты">
              <Row label="Расчётный счёт">{detail.bankAccount ?? '—'}</Row>
              <Row label="БИК">{detail.bik ?? '—'}</Row>
              <Row label="Банк">{detail.bankName ?? '—'}</Row>
              <Row label="Корр. счёт">{detail.corrAccount ?? '—'}</Row>
            </Section>

            <Section title="Каталог и логистика">
              <Row label="Товаров / категорий">
                {detail.productsCount} / {detail.categoriesCount}
              </Row>
              <Row label="Регионы доставки">
                {detail.deliveryRegions.length ? detail.deliveryRegions.join(', ') : '—'}
              </Row>
              <Row label="График">{detail.deliverySchedule ?? '—'}</Row>
              <Row label="Мин. заказ">
                {detail.minOrderAmount != null ? formatKop(detail.minOrderAmount) : '—'}
              </Row>
            </Section>

            <div className="flex flex-wrap justify-end gap-2 border-t border-line pt-4">
              {detail.status === 'pending' && (
                <button
                  disabled={busy}
                  onClick={() => onAct(detail.id, 'ACTIVE', detail.name)}
                  className="inline-flex items-center gap-1.5 border border-green-600 bg-green-600 px-4 py-2 text-[13px] font-medium text-white hover:bg-green-700 disabled:opacity-50"
                >
                  <Check size={15} /> Одобрить
                </button>
              )}
              {(detail.status === 'pending' || detail.status === 'active') && (
                <button
                  disabled={busy}
                  onClick={() => onAct(detail.id, 'BLOCKED', detail.name)}
                  className="inline-flex items-center gap-1.5 border border-red-300 px-4 py-2 text-[13px] font-medium text-red-700 hover:bg-red-50 disabled:opacity-50"
                >
                  <Ban size={15} /> {detail.status === 'pending' ? 'Отклонить' : 'Заблокировать'}
                </button>
              )}
              {detail.status === 'blocked' && (
                <button
                  disabled={busy}
                  onClick={() => onAct(detail.id, 'ACTIVE', detail.name)}
                  className="inline-flex items-center gap-1.5 border border-green-600 px-4 py-2 text-[13px] font-medium text-green-700 hover:bg-green-50 disabled:opacity-50"
                >
                  <RotateCcw size={15} /> Разблокировать
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h3 className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-ink/40">{title}</h3>
      <dl className="divide-y divide-line border border-line">{children}</dl>
    </div>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-4 px-3 py-2 text-[13px]">
      <dt className="text-ink/50">{label}</dt>
      <dd className="text-right text-ink/90">{children}</dd>
    </div>
  );
}
